import { faLock } from "@fortawesome/pro-regular-svg-icons";
import { twJoin } from "tailwind-merge";

import { Icon } from "src/base-components/Icon";
import { Address } from "src/clients/entities/api";
import { Tooltip } from "src/design-system/Tooltip";
import {
  JsonContent,
  renderAddress,
} from "src/entities/entityView/EntityDetailsSidePane";

export type SensitiveFieldValue = string | Address | null;

const renderSensitiveContent = (
  value: SensitiveFieldValue,
  propertyName: string,
  displayJsonFormat: boolean,
  isRevealed: boolean,
): React.ReactNode => {
  if (!value) return null;

  if (typeof value === "string") {
    return value;
  }

  if (!displayJsonFormat && value && typeof value === "object") {
    if (isRevealed) {
      return renderAddress(value as Address);
    }

    const [firstKey] = Object.keys(value);
    return firstKey ? value[firstKey as keyof typeof value] : null;
  }

  return <JsonContent content={value} title={propertyName} />;
};

const getTooltipContent = (
  value: SensitiveFieldValue,
  displayJsonFormat: boolean,
): string => {
  if (!value) return "";

  if (typeof value === "object") {
    if (displayJsonFormat) {
      return JSON.stringify(value);
    }

    const [firstKey] = Object.keys(value);
    return value[firstKey as keyof typeof value] ?? "";
  }

  return value;
};

type SensitiveFieldProps = {
  value: SensitiveFieldValue;
  propertyName: string;
  displayJsonFormat?: boolean;
  displayInfoIcon?: boolean;
  isRevealed?: boolean;
};

export const SensitiveField: React.FC<SensitiveFieldProps> = ({
  value,
  propertyName,
  displayJsonFormat = true,
  displayInfoIcon = true,
  isRevealed = false,
}) => {
  return (
    <div
      className={twJoin(
        "group flex w-full items-center justify-between",
        !isRevealed && "text-gray-500 font-code-12",
      )}
    >
      <Tooltip
        disabled={!isRevealed}
        placement="top"
        title={getTooltipContent(value, displayJsonFormat)}
        asChild
      >
        <div className={twJoin(!isRevealed && "truncate")}>
          {renderSensitiveContent(
            value,
            propertyName,
            displayJsonFormat,
            isRevealed,
          )}
        </div>
      </Tooltip>
      <div className="flex">
        {displayInfoIcon && (
          <div className="opacity-0 transition-opacity group-hover:opacity-100">
            <Tooltip title="Masked field">
              <Icon icon={faLock} size="xs" />
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  );
};
